import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserProjectAccess } from "./UserProjectAccess";
import { UserProjectAccessState } from "./UserProjectAccessState";
import {
  fetchProjectToAccess,
  fetchUserProjectAccesses,
  saveAccessUserProjects,
} from "./accessUserProjectApi";
import { RootState } from "../../app/store";
import { ProjectToAccess } from "./ProjectToAccess";
import { ObjectState } from "../ObjectState";

const initialState: UserProjectAccessState = {
  availableProjects: [],
  userProjectAccesses: [],
  initializationStatus: "not-initialized",
  status: "idle",
};

const accessUserProjectSlice = createSlice({
  name: "userProjectAccess",
  initialState,
  reducers: {
    addUserProjectAccess: (state, action: PayloadAction<UserProjectAccess>) => {
      state.userProjectAccesses.push(action.payload);
    },
    updateUserProjectAccess: (
      state,
      action: PayloadAction<{ userId: string; project: ProjectToAccess }>
    ) => {
      const { userId, project } = action.payload;
      const userAccess = state.userProjectAccesses.find(
        (access) => access.userId === userId
      );
      if (typeof userAccess !== "undefined") {
        const existProject = userAccess.projectsToAccess.find(
          (p) => p.id === project.id
        );
        if (typeof existProject !== "undefined") {
          const existProjectObjectState = existProject.objectState;
          if (existProjectObjectState === ObjectState.Original) {
            if (project.objectState === ObjectState.Original) {
              userAccess.projectsToAccess = userAccess.projectsToAccess.map(
                (p) =>
                  p.id === project.id
                    ? { ...p, objectState: ObjectState.Deleted }
                    : p
              );
            } else if (project.objectState === ObjectState.Added) {
              userAccess.projectsToAccess = userAccess.projectsToAccess.map(
                (p) =>
                  p.id === project.id
                    ? { ...p, objectState: ObjectState.Added }
                    : p
              );
            } else if (project.objectState === ObjectState.Deleted) {
              userAccess.projectsToAccess = userAccess.projectsToAccess.map(
                (p) =>
                  p.id === project.id
                    ? { ...p, objectState: ObjectState.Original }
                    : p
              );
            }
          } else if (existProjectObjectState === ObjectState.Added) {
            userAccess.projectsToAccess = userAccess.projectsToAccess.filter(
              (p) => p.id !== project.id
            );
          } else if (existProjectObjectState === ObjectState.Deleted) {
            userAccess.projectsToAccess = userAccess.projectsToAccess.map((p) =>
              p.id === project.id
                ? { ...p, objectState: ObjectState.Original }
                : p
            );
          }
        } else {
          const newProject = { ...project, objectState: ObjectState.Added };
          userAccess.projectsToAccess.push(newProject);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProjectAccesses.pending, (state) => {
        state.initializationStatus = "initializing";
        state.status = "loading";
      })
      .addCase(fetchUserProjectAccesses.fulfilled, (state, action) => {
        state.initializationStatus = "initialized";
        state.userProjectAccesses = action.payload;
        state.status = "idle";
      })
      .addCase(fetchUserProjectAccesses.rejected, (state) => {
        state.initializationStatus = "initialized";
        state.status = "failed";
      })
      .addCase(fetchProjectToAccess.pending, (state) => {
        state.initializationStatus = "initializing";
        state.status = "loading";
      })
      .addCase(fetchProjectToAccess.fulfilled, (state, action) => {
        state.availableProjects = action.payload;
        state.initializationStatus = "initialized";
        state.status = "idle";
      })
      .addCase(fetchProjectToAccess.rejected, (state) => {
        state.initializationStatus = "initialized";
        state.status = "failed";
      })
      .addCase(saveAccessUserProjects.pending, (state) => {
        state.status = "loading";
      })
      .addCase(saveAccessUserProjects.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(saveAccessUserProjects.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { addUserProjectAccess, updateUserProjectAccess } =
  accessUserProjectSlice.actions;

export const selectProjectToAccess = (state: RootState) =>
  state.userProjectAccess.availableProjects;

export const selectUserProjectAccess = (state: RootState) =>
  state.userProjectAccess.userProjectAccesses;

export const selectIsLoading = (state: RootState) => {
  return state.userProjectAccess.status === "loading";
};

export const hasProjectAccessChanges = (state: RootState): boolean => {
  return state.userProjectAccess.userProjectAccesses.some(
    (userProjectAccess) =>
      userProjectAccess.projectsToAccess.length > 0 &&
      userProjectAccess.projectsToAccess.some(
        (project) => project.objectState !== ObjectState.Original
      )
  );
};

export default accessUserProjectSlice.reducer;
